import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Printer, Search, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { rupiah, outletNames, today } from "../../lib/format";
import { useOutlets } from "../../lib/useOutlets";
import { useAuth } from "../../context/AuthContext";
import { exportCSV } from "../../lib/exportCsv";
import { printReport, escapeHtml } from "../../lib/printDocs";

const deptName = (code) => outletNames[code] || code || "-";
const firstOfMonth = () => today().slice(0, 8) + "01";
const formatDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "-");
const num = (v) => new Intl.NumberFormat("id-ID", { maximumFractionDigits: 4 }).format(Number(v || 0));

/* ------------------------------ shared bits ------------------------------ */
function DeptMultiSelect({ selected, onChange, testid }) {
  const outlets = useOutlets().filter((o) => o.code !== "main_wh" && o.type !== "warehouse");
  const toggle = (code) =>
    onChange(selected.includes(code) ? selected.filter((c) => c !== code) : [...selected, code]);
  return (
    <label className="field" style={{ flex: "1 1 320px" }}>
      <span>Department {selected.length > 0 ? `(${selected.length} selected)` : "(all)"}</span>
      <div className="multi-select" data-testid={testid}>
        {outlets.map((o) => (
          <button
            key={o.code}
            type="button"
            data-testid={`${testid}-${o.code}`}
            className={`chip small ${selected.includes(o.code) ? "green" : ""}`}
            onClick={() => toggle(o.code)}
          >
            {o.name}
          </button>
        ))}
        {selected.length > 0 && (
          <button type="button" data-testid={`${testid}-clear`} className="chip small" onClick={() => onChange([])}>
            <X size={11} /> Clear
          </button>
        )}
      </div>
    </label>
  );
}

function ReportActions({ onCsv, onPdf, disabled }) {
  return (
    <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
      <button data-testid="report-export-btn" className="secondary-button" onClick={onCsv} disabled={disabled}>
        <Download size={14} /> Export Excel (CSV)
      </button>
      <button data-testid="report-print-btn" className="secondary-button" onClick={onPdf} disabled={disabled}>
        <Printer size={14} /> Export PDF
      </button>
    </div>
  );
}

function Tile({ label, value, tone, testid }) {
  return (
    <div className="summary-tile">
      <span>{label}</span>
      <strong className={tone || ""} data-testid={testid}>{value}</strong>
    </div>
  );
}

const R = { textAlign: "right" };

/* --------------------------- A. Stock Out Detail --------------------------- */
export function StockOutDetail() {
  const { user } = useAuth();
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(today());
  const [depts, setDepts] = useState([]);
  const [item, setItem] = useState("");
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!start || !end) return;
    setLoading(true);
    api
      .get("/reports/stock-out-detail", {
        params: { start, end, departments: depts.join(",") || undefined, item: item || undefined, category: category || undefined },
      })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [start, end, depts, item, category]);

  // Flatten → search → regroup so subtotals always reflect the visible rows.
  const groups = useMemo(() => {
    if (!data) return { days: [], qty: 0, value: 0, count: 0 };
    const s = q.trim().toLowerCase();
    const flat = data.days.flatMap((d) => d.departments.flatMap((dep) => dep.rows));
    const rows = s
      ? flat.filter((r) =>
          [r.issue_no, deptName(r.department), r.item_code, r.item_name, r.category, r.unit, r.date]
            .some((v) => (v || "").toLowerCase().includes(s)))
      : flat;
    const days = [];
    for (const r of rows) {
      let day = days[days.length - 1];
      if (!day || day.date !== r.date) {
        day = { date: r.date, departments: [], qty: 0, value: 0 };
        days.push(day);
      }
      let dep = day.departments[day.departments.length - 1];
      if (!dep || dep.department !== r.department) {
        dep = { department: r.department, rows: [], qty: 0, value: 0 };
        day.departments.push(dep);
      }
      dep.rows.push(r);
      dep.qty += r.qty; dep.value += r.total_value;
      day.qty += r.qty; day.value += r.total_value;
    }
    return { days, qty: rows.reduce((a, r) => a + r.qty, 0), value: rows.reduce((a, r) => a + r.total_value, 0), count: rows.length };
  }, [data, q]);

  const csvRows = () => {
    const out = [];
    groups.days.forEach((d) => {
      d.departments.forEach((dep) => {
        dep.rows.forEach((r) => out.push([r.date, r.issue_no, deptName(r.department), r.item_code, r.item_name, r.category, r.unit, r.qty, r.unit_cost, r.total_value]));
        out.push([d.date, "", `Subtotal ${deptName(dep.department)}`, "", "", "", "", dep.qty, "", Math.round(dep.value)]);
      });
      out.push([d.date, "", "Subtotal day", "", "", "", "", d.qty, "", Math.round(d.value)]);
    });
    out.push(["", "", "GRAND TOTAL", "", "", "", "", groups.qty, "", Math.round(groups.value)]);
    return out;
  };
  const doCsv = () =>
    exportCSV(`stock-out-detail-${start}_${end}.csv`,
      ["Date", "Issue No", "Department", "Item Code", "Item Name", "Category", "Unit", "Qty", "Unit Cost", "Total Value"], csvRows());

  const doPdf = () => {
    const body = [];
    groups.days.forEach((d) => {
      body.push(`<tr><td colspan="10" style="background:#e9eeec;font-weight:700">${fmt(d.date)}</td></tr>`);
      d.departments.forEach((dep) => {
        body.push(`<tr><td colspan="10" style="background:#f4f7f5;font-weight:600;padding-left:18px">${escapeHtml(deptName(dep.department))}</td></tr>`);
        dep.rows.forEach((r) =>
          body.push(`<tr><td>${fmt(r.date)}</td><td>${escapeHtml(r.issue_no)}</td><td>${escapeHtml(deptName(r.department))}</td><td>${escapeHtml(r.item_code)}</td><td>${escapeHtml(r.item_name)}</td><td>${escapeHtml(r.category)}</td><td>${escapeHtml(r.unit)}</td><td class="num">${num(r.qty)}</td><td class="num">${rupiah(r.unit_cost)}</td><td class="num">${rupiah(r.total_value)}</td></tr>`));
        body.push(`<tr><td colspan="7" style="text-align:right;font-weight:600">Subtotal ${escapeHtml(deptName(dep.department))}</td><td class="num" style="font-weight:600">${num(dep.qty)}</td><td></td><td class="num" style="font-weight:600">${rupiah(dep.value)}</td></tr>`);
      });
      body.push(`<tr><td colspan="7" style="text-align:right;font-weight:700;background:#f4f7f5">Subtotal ${fmt(d.date)}</td><td class="num" style="font-weight:700;background:#f4f7f5">${num(d.qty)}</td><td style="background:#f4f7f5"></td><td class="num" style="font-weight:700;background:#f4f7f5">${rupiah(d.value)}</td></tr>`);
    });
    printReport({
      title: "Stock Out Detail Report",
      period: { start, end },
      printedBy: user ? `${user.name} (@${user.username})` : "",
      filters: [
        { label: "Department", value: depts.length ? depts.map(deptName).join(", ") : "All" },
        { label: "Item", value: item }, { label: "Category", value: category }, { label: "Search", value: q },
      ],
      columns: [
        { label: "Date" }, { label: "Issue No" }, { label: "Department" }, { label: "Item Code" }, { label: "Item Name" },
        { label: "Category" }, { label: "Unit" }, { label: "Qty", className: "num" }, { label: "Unit Cost", className: "num" }, { label: "Total Value", className: "num" },
      ],
      bodyRows: body,
      footerRow: body.length ? `<tr><td colspan="7">GRAND TOTAL</td><td class="num">${num(groups.qty)}</td><td></td><td class="num">${rupiah(groups.value)}</td></tr>` : null,
    });
  };

  return (
    <>
      <div className="report-bar">
        <label className="field small"><span>From *</span>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start" required />
        </label>
        <label className="field small"><span>To *</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end" required />
        </label>
        <label className="field small"><span>Item (code / name)</span>
          <input data-testid="stock-out-item" value={item} onChange={(e) => setItem(e.target.value)} placeholder="All" />
        </label>
        <label className="field small"><span>Category</span>
          <input data-testid="stock-out-category" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="All" />
        </label>
        <DeptMultiSelect selected={depts} onChange={setDepts} testid="stock-out-dept" />
      </div>
      <div className="report-bar">
        <div className="report-summary">
          <Tile label="Lines" value={groups.count} testid="stock-out-detail-lines" />
          <Tile label="Total qty" value={num(groups.qty)} testid="stock-out-detail-qty" />
          <Tile label="Total value" value={rupiah(groups.value)} tone="teal" testid="stock-out-detail-total" />
        </div>
        <div className="search-box" style={{ maxWidth: 300 }}>
          <Search size={15} />
          <input data-testid="stock-out-detail-search" placeholder="Search result table..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <ReportActions onCsv={doCsv} onPdf={doPdf} disabled={groups.count === 0} />
      </div>
      <section className="panel">
        <div className="table-wrap">
          <table data-testid="stock-out-detail-table">
            <thead>
              <tr>
                <th>Date</th><th>Issue No</th><th>Department</th><th>Item Code</th><th>Item Name</th><th>Category</th><th>Unit</th>
                <th style={R}>Qty</th><th style={R}>Unit Cost</th><th style={R}>Total Value</th>
              </tr>
            </thead>
            <tbody>
              {loading && !data && <tr><td colSpan={10} style={{ textAlign: "center", padding: 40 }}>Loading...</td></tr>}
              {data && groups.count === 0 && (
                <tr><td colSpan={10} style={{ textAlign: "center", padding: 40 }} data-testid="stock-out-detail-empty">
                  No data for the selected period / filters.
                </td></tr>
              )}
              {groups.days.map((d) => (
                <React.Fragment key={d.date}>
                  <tr className="report-group-row"><td colSpan={10}>{formatDate(d.date)}</td></tr>
                  {d.departments.map((dep) => (
                    <React.Fragment key={d.date + dep.department}>
                      <tr className="report-group-row"><td colSpan={10} style={{ paddingLeft: 32, textTransform: "none" }}>{deptName(dep.department)}</td></tr>
                      {dep.rows.map((r, i) => (
                        <tr key={i} data-testid="stock-out-detail-row">
                          <td><small>{formatDate(r.date)}</small></td>
                          <td><strong>{r.issue_no}</strong>{r.type === "direct" && <small> direct</small>}</td>
                          <td>{deptName(r.department)}</td>
                          <td>{r.item_code || "-"}</td>
                          <td><strong>{r.item_name}</strong></td>
                          <td>{r.category || "-"}</td>
                          <td>{r.unit}</td>
                          <td style={R}>{num(r.qty)}</td>
                          <td style={R}>{rupiah(r.unit_cost)}</td>
                          <td style={R}><strong>{rupiah(r.total_value)}</strong></td>
                        </tr>
                      ))}
                      <tr className="report-subtotal-row">
                        <td colSpan={7} style={R}>Subtotal {deptName(dep.department)}</td>
                        <td style={R}>{num(dep.qty)}</td><td></td><td style={R}>{rupiah(dep.value)}</td>
                      </tr>
                    </React.Fragment>
                  ))}
                  <tr className="report-subtotal-row">
                    <td colSpan={7} style={R}>Subtotal {formatDate(d.date)}</td>
                    <td style={R}>{num(d.qty)}</td><td></td><td style={R}>{rupiah(d.value)}</td>
                  </tr>
                </React.Fragment>
              ))}
              {groups.count > 0 && (
                <tr className="report-total-row" data-testid="stock-out-detail-grand-total">
                  <td colSpan={7} style={R}>GRAND TOTAL</td>
                  <td style={R}>{num(groups.qty)}</td><td></td><td style={R}>{rupiah(groups.value)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

const fmt = (d) => formatDate(d);

/* --------------------------- B. Stock Out Summary -------------------------- */
export function StockOutSummary() {
  const { user } = useAuth();
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(today());
  const [depts, setDepts] = useState([]);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!start || !end) return;
    api
      .get("/reports/stock-out-summary", { params: { start, end, departments: depts.join(",") || undefined } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [start, end, depts]);

  const rows = data?.rows || [];
  const t = data?.totals || {};

  const doCsv = () =>
    exportCSV(`stock-out-summary-${start}_${end}.csv`,
      ["Department", "Number of Transactions", "Total Value", "% of Total"],
      [...rows.map((r) => [deptName(r.department), r.transactions, Math.round(r.total_value), r.pct]),
        ["GRAND TOTAL", t.transactions, Math.round(t.total_value || 0), rows.length ? 100 : 0]]);

  const doPdf = () =>
    printReport({
      title: "Stock Out Summary Report",
      period: { start, end },
      printedBy: user ? `${user.name} (@${user.username})` : "",
      filters: [{ label: "Department", value: depts.length ? depts.map(deptName).join(", ") : "All" }],
      columns: [{ label: "Department" }, { label: "Transactions", className: "num" }, { label: "Total Value", className: "num" }, { label: "% of Total", className: "num" }],
      bodyRows: rows.map((r) => `<tr><td>${escapeHtml(deptName(r.department))}</td><td class="num">${r.transactions}</td><td class="num">${rupiah(r.total_value)}</td><td class="num">${r.pct.toFixed(2)}%</td></tr>`),
      footerRow: rows.length ? `<tr><td>GRAND TOTAL</td><td class="num">${t.transactions}</td><td class="num">${rupiah(t.total_value)}</td><td class="num">100%</td></tr>` : null,
    });

  return (
    <>
      <div className="report-bar">
        <label className="field small"><span>From *</span>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start" required />
        </label>
        <label className="field small"><span>To *</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end" required />
        </label>
        <DeptMultiSelect selected={depts} onChange={setDepts} testid="stock-out-summary-dept" />
        <div className="report-summary">
          <Tile label="Departments" value={t.department_count || 0} testid="stock-out-summary-depts" />
          <Tile label="Transactions" value={t.transactions || 0} testid="stock-out-summary-trx" />
          <Tile label="Total value" value={rupiah(t.total_value)} tone="teal" testid="stock-out-summary-total" />
        </div>
        <ReportActions onCsv={doCsv} onPdf={doPdf} disabled={rows.length === 0} />
      </div>
      <section className="panel">
        <div className="table-wrap">
          <table data-testid="stock-out-summary-table">
            <thead>
              <tr><th>Department</th><th style={R}>Number of Transactions</th><th style={R}>Total Value</th><th style={R}>% of Total</th></tr>
            </thead>
            <tbody>
              {!data && <tr><td colSpan={4} style={{ textAlign: "center", padding: 40 }}>Loading...</td></tr>}
              {data && rows.length === 0 && (
                <tr><td colSpan={4} style={{ textAlign: "center", padding: 40 }} data-testid="stock-out-summary-empty">No data for the selected period / filters.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.department} data-testid={`stock-out-summary-row-${r.department}`}>
                  <td><strong>{deptName(r.department)}</strong></td>
                  <td style={R}>{r.transactions}</td>
                  <td style={R}><strong>{rupiah(r.total_value)}</strong></td>
                  <td style={R}>{r.pct.toFixed(2)}%</td>
                </tr>
              ))}
              {rows.length > 0 && (
                <tr className="report-total-row" data-testid="stock-out-summary-grand-total">
                  <td>GRAND TOTAL</td>
                  <td style={R}>{t.transactions}</td>
                  <td style={R}>{rupiah(t.total_value)}</td>
                  <td style={R}>100%</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
